import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  Button, TextField, Typography,
  Box,
} from "@mui/material";
import BootstrapTooltip from "../components/tooltip";
import { exportToExcel } from "./exportExcel";
import dayjs from "dayjs";

const ImportExcelModal = ({ open, onClose, onSave, userSchedules, showAlert, userRoute, geofences }) => {
  const [file, setFile] = useState(null);
  const [showSpecs, setShowSpecs] = useState(false);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleImport = () => {
    if (!file) {
      console.error("No se seleccionó ningún archivo.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const schedules = parseExcelData(rawData);

      const hasInvalidTime = schedules.some(schedule =>
        schedule.turns.some(turn =>
          turn.some(point => isNaN(point.time))
        )
      );

      if (hasInvalidTime) {
        showAlert("El archivo contiene valores erróneos. Corrija los datos asegurando un formato válido y vuelva a intentarlo.", "error");
        return;
      }

      onSave(schedules);
    };

    reader.readAsArrayBuffer(file);
    setFile(null);
  };

  const parseExcelData = (rawData) => {
    const schedules = [];
    let currentSchedule = null;
    let controlPoints = [];

    const usedNames = new Set(userSchedules.map(s => s.name));
    let scheduleCounter = 1;

    const getUniqueScheduleName = (base = "Horario") => {
      while (usedNames.has(`${base} ${scheduleCounter}`)) {
        scheduleCounter++;
      }
      const name = `${base} ${scheduleCounter}`;
      usedNames.add(name);
      return name;
    };

    rawData.forEach((row) => {
      if (row.length === 0) {
        if (currentSchedule) {
          schedules.push(currentSchedule);
          currentSchedule = null;
          controlPoints = [];
        }
        return;
      }

      const nonEmptyCells = row.filter(cell => cell !== null && cell !== undefined && cell !== '').length;

      if (!currentSchedule) {
        if (nonEmptyCells === 1) {
          const proposedName = row[0];
          const name = usedNames.has(proposedName) ? getUniqueScheduleName() : proposedName;
          usedNames.add(name);
          currentSchedule = { name, turns: [] };
        } else {
          const name = getUniqueScheduleName();
          currentSchedule = { name, turns: [] };
          controlPoints = row;
        }
      } else if (controlPoints.length === 0) {
        controlPoints = row;
      } else {
        const turn = controlPoints.map((label, index) => {
          const value = row[index];

          return {
            label: label || "",
            time:
              value === undefined ||
                value === null ||
                String(value).trim() === ""
                ? null
                : parseTime(value)
          };
        });
        currentSchedule.turns.push(turn);
      }
    });

    if (currentSchedule) schedules.push(currentSchedule);
    return schedules;
  };

  const parseTime = (timeValue) => {
    if (typeof timeValue === "number") {
      if (!Number.isFinite(timeValue)) return NaN;

      const secondsInDay = 24 * 60 * 60;
      const dayFraction = ((timeValue % 1) + 1) % 1;
      return Math.round(dayFraction * secondsInDay) % secondsInDay;
    }

    if (timeValue instanceof Date && !isNaN(timeValue.getTime())) {
      return timeValue.getHours() * 3600 + timeValue.getMinutes() * 60 + timeValue.getSeconds();
    }

    if (typeof timeValue !== "string") return NaN;

    let time = timeValue.trim().toLowerCase();

    // Detectar AM/PM
    const isPM = /(p\.?\s*m\.?|pm)/.test(time);
    const isAM = /(a\.?\s*m\.?|am)/.test(time);

    // Eliminar el sufijo AM/PM
    time = time.replace(/\s*(a|p)\.?\s*m\.?/gi, "").trim();

    const parts = time.split(":");
    if (parts.length < 2 || parts.length > 3) return NaN;

    let hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts.length === 3 ? Number(parts[2]) : 0;

    if ([hours, minutes, seconds].some(isNaN)) return NaN;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return NaN;

    if (isPM && hours < 12) hours += 12;
    if (isAM && hours === 12) hours = 0;

    return hours * 3600 + minutes * 60 + seconds;
  };

  const handleClose = () => {
    setFile(null);
    onClose();
  }

  const getTotalSeconds = (diff) => (diff?.minutes ?? 0) * 60 + (diff?.seconds ?? 0);

  const generateSchedule = () => {
    const startTimeParam = dayjs().hour(12).minute(0).second(0);
    const route = userRoute.filter(geofence => geofence.state === 'activo').map(geofence => geofence.label)
    const cpStart = route[0];
    const cpEnd = route[route.length - 1];
    const startIndex = route.indexOf(cpStart);
    const endIndex = route.indexOf(cpEnd);

    const numLaps = 8;

    const newTableData = [];
    let currentLapTime = dayjs(startTimeParam);

    for (let lap = 0; lap < numLaps; lap++) {
      const lapData = [];

      for (let index = 0; index < route.length; index++) {
        const label = route[index];
        const point = geofences.find((gf) => gf.label === label.replace(/\s(Ida|Vuelta)$/, ""));
        let time = "-";
        const diffObj = { minutes: 5, seconds: 0 };
        const secondsToAdd = getTotalSeconds(diffObj);

        if (numLaps === 1) {
          if (index >= startIndex && index <= endIndex) {
            time = currentLapTime.format("HH:mm:ss");
            currentLapTime = currentLapTime.add(secondsToAdd, "second");
          }
        } else if (lap === 0) {
          if (index >= startIndex) {
            time = currentLapTime.format("HH:mm:ss");
            currentLapTime = currentLapTime.add(secondsToAdd, "second");
          }
        } else if (lap > 0 && lap < numLaps - 1) {
          time = currentLapTime.format("HH:mm:ss");
          currentLapTime = currentLapTime.add(secondsToAdd, "second");
        } else if (lap === numLaps - 1) {
          if (index <= endIndex) {
            time = currentLapTime.format("HH:mm:ss");
            currentLapTime = currentLapTime.add(secondsToAdd, "second");
          }
        }

        lapData.push({
          id: point?.id || index,
          label,
          time,
        });
      }

      newTableData.push(lapData);
    }

    const tableDataInSeconds = newTableData.map((lap) =>
      lap.map((point) => {
        if (point.time === "-") {
          return { ...point, time: null };
        }

        const [hours, minutes, seconds] = point.time.split(":").map(Number);
        const timeInSeconds = hours * 3600 + minutes * 60 + seconds;
        return { ...point, time: timeInSeconds };
      })
    );

    exportToExcel([
      {
        name: "[Nombre del horario]",
        turns: tableDataInSeconds
      },
    ]);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{
        sx: {
          backgroundColor: "var(--color-background)",
        }
      }} >
        <DialogTitle sx={{ color: "var(--color-dark)" }}>
          Importar Archivo Excel
          <BootstrapTooltip title='Especificaciones del formato válido'>
            <Button
              onClick={() => setShowSpecs(true)}
              sx={{ float: 'inline-end', padding: '0', minWidth: 'auto' }}
            >
              <span className="material-symbols-rounded icon" >help</span>
            </Button>
          </BootstrapTooltip>
        </DialogTitle>
        <DialogContent>
          <TextField
            type="file"
            fullWidth
            onChange={handleFileChange}
            slotProps={{
              htmlInput: { accept: ".xlsx, .xls" },
              input: {
                sx: {
                  '& input::file-selector-button': {
                    color: 'var(--color-dark)',
                    backgroundColor: 'var(--color-dark-light)',
                    border: 'none',
                    padding: '4px 12px',
                    cursor: 'pointer'
                  },
                  '& input': {
                    color: 'var(--color-dark)'
                  }
                }
              }
            }}
          />

        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} sx={{ color: 'var(--color-danger)' }}>
            Cancelar
          </Button>
          <Button onClick={handleImport} color="primary" variant="contained" disabled={!file}>
            Importar
          </Button>
        </DialogActions>
      </Dialog>

      <FormatSpecsModal open={showSpecs} onClose={() => setShowSpecs(false)} generateSchedule={generateSchedule} />
    </>
  );
};

const FormatSpecsModal = ({ open, onClose, generateSchedule }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{
    sx: { backgroundColor: "var(--color-background)", borderRadius: 3 }
  }}>
    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "var(--color-dark)" }}>
      <span className="material-symbols-rounded icon">help</span>
      Especificaciones del Formato de Importación
    </DialogTitle>
    <DialogContent dividers sx={{ maxHeight: "60vh" }}>
      <Box
        sx={{
          backgroundColor: "var(--color-dark-light)",
          borderRadius: 2,
          padding: 2,
        }}
      >
        <Typography variant="body1" component="div" sx={{ color: "var(--color-dark)", fontSize: "1rem" }}>
          <ul style={{ paddingLeft: "1.2em", marginTop: 0 }}>
            <li style={{ marginBottom: "0.8em" }}>
              La <strong>primera fila</strong> debe contener una única celda con el <strong>nombre del horario</strong>.
              En caso de que no se especifique un nombre o este se repita en los horarios guardados, se asignará un nombre por defecto siguiendo una secuencia irrepetible.
            </li>
            <li style={{ marginBottom: "0.8em" }}>
              La <strong>segunda fila</strong> debe contener los <strong>nombres exactos de los puntos de control </strong>
              registrados en el sistema (geocercas). Si se define un nombre que no corresponda con alguno de los puntos de control registrados, se marcará un error.
            </li>
            <li style={{ marginBottom: "0.8em" }}>
              Las siguientes filas deben contener los <strong>tiempos de llegada</strong> en formato <code>HH:MM</code>.
              <br />Ejemplos válidos: <code>07:00</code>, <code>15:00</code>.
            </li>
            <li>
              <strong>No debe haber filas en blanco</strong> entre las filas de los tiempos de llegada.
              Una fila vacía marca el fin del horario actual e inicia el análisis del siguiente.
            </li>
          </ul>
        </Typography>
      </Box>
    </DialogContent>
    <DialogActions>
      {/* <Button
        component="a"
        href="/Horarios - plantilla.xlsx"
        download
        variant="outlined"
        color="primary"
      >
        Descargar Plantilla
      </Button> */}
      <Button
        onClick={onClose}
        sx={{ color: 'var(--color-danger)' }}
      >
        Cerrar
      </Button>
      <Button
        onClick={generateSchedule}
        download
        variant="contained"
        color="primary"
      >
        Descargar Plantilla
      </Button>
    </DialogActions>
  </Dialog>
);


export default ImportExcelModal;
